import type { CanvasDefinition, CanvasStepStats } from '../../../api/client.ts';

type Graph = Pick<CanvasDefinition, 'variants' | 'steps'>;

const FIXED_PORTS: Record<string, string> = { everyone_else: 'Everyone Else', next: 'Next', yes: 'Yes', no: 'No', sent: 'Sent', migration: 'Moved by a migration' };

/** A port as the marketer named it: the paths group's name, «Everyone Else», «Yes» / «No»; else the port id. */
export function portLabel(graph: Graph | null, stepId: string, port: string): string {
  const config = graph?.steps[stepId]?.config as { groups?: Array<{ id?: unknown; name?: unknown }> } | undefined;
  const group = Array.isArray(config?.groups) ? config.groups.find((g) => g.id === port) : undefined;
  if (group && typeof group.name === 'string' && group.name) return group.name;
  return FIXED_PORTS[port] ?? port;
}

/** Exit reasons of `canvas_entry.exit_reason` (BB-112, BB-117, BB-118, BB-121). */
const EXIT_REASONS: Record<string, string> = {
  message_suppressed: 'Message suppressed',
  exit_audience: 'Left the exit audience',
  action_paths_exit: 'Exited from Action Paths',
  decision_split_no: 'Decision Split «No» exits',
  control_group: 'Control group',
  canvas_stopped: 'Canvas stopped',
  migration_exit: 'Exited by a migration',
  no_next_step: 'No next step',
  delay_past: 'Delay date already passed',
  personalization_failed: 'Personalization failed',
  provider_rejected: 'Rejected by the provider',
  wait_without_wakeup: 'Waited without a wake-up',
  merged: 'Profile merged',
  exited: 'Exited',
};

export function exitReasonLabel(reason: string): string {
  if (reason.startsWith('exit_event:')) return `Exit event ${reason.slice('exit_event:'.length)}`;
  return EXIT_REASONS[reason] ?? reason;
}

/** `5 s`, `2 min 3 s`, `1 day 4 h`: at most the two largest units, stopping at the first empty one. */
export function formatSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  const parts: string[] = [];
  let left = Math.round(seconds);
  for (const [size, one, many] of [
    [86_400, 'day', 'days'],
    [3_600, 'h', 'h'],
    [60, 'min', 'min'],
    [1, 's', 's'],
  ] as const) {
    const n = Math.floor(left / size);
    left -= n * size;
    if (n) parts.push(`${n} ${n === 1 ? one : many}`);
    else if (parts.length) break;
    if (parts.length === 2) break;
  }
  return parts.join(' ') || '0 s';
}

export const sum = (counts: Record<string, number>) => Object.values(counts).reduce((a, b) => a + b, 0);

/** A Message step that sends push (Android or iOS) in the graph; a step the graph no longer has may have sent push. */
export function sendsPush(graph: Graph | null, step: Pick<CanvasStepStats, 'stepId' | 'type'>): boolean {
  if (step.type !== 'message') return false;
  const known = graph?.steps[step.stepId];
  if (!known) return true;
  const channels = (known.config as { channels?: unknown } | null)?.channels;
  return Array.isArray(channels) && channels.some((c) => c === 'push_android' || c === 'push_ios');
}

/**
 * Steps in journey order: breadth-first from the variants' start steps along the ports, then the steps of the graph
 * nothing leads to, then steps only the data knows (removed in later versions).
 */
export function orderSteps(graph: Graph | null, steps: CanvasStepStats[]): Array<CanvasStepStats & { inGraph: boolean }> {
  const order: string[] = [];
  if (graph) {
    const seen = new Set<string>();
    const queue = graph.variants.flatMap((v) => (v.startStepId ? [v.startStepId] : []));
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id) || !graph.steps[id]) continue;
      seen.add(id);
      order.push(id);
      for (const n of graph.steps[id]!.next) if (n.stepId) queue.push(n.stepId);
    }
    for (const id of Object.keys(graph.steps)) if (!seen.has(id)) order.push(id);
  }
  const rank = new Map(order.map((id, i) => [id, i]));
  return steps
    .map((s) => ({ ...s, inGraph: rank.has(s.stepId) }))
    .sort((a, b) => (rank.get(a.stepId) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.stepId) ?? Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name));
}
